/**
 * Google Ads + Meta Pixel + GTM dataLayer conversion tracking utilities.
 *
 * Event vocabulary used by CTA buttons across the site (KAN-28):
 *   - cta_call_click      — phone (tel:) link clicks
 *   - cta_sms_click       — SMS  (sms:) link clicks
 *   - cta_booking_click   — Book Now CTAs that route to /book
 *
 * Each helper fans out to GA4 (gtag), Meta Pixel (fbq), and GTM (dataLayer)
 * so dashboards on either platform have a single source of truth.
 */

const GA_TRACKING_ID = 'AW-18062345351';

const BOOKING_PATH = '/book';
const BOOKING_UTM_BASE = 'utm_source=site&utm_medium=cta&utm_campaign=booking';

function gtag() {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag.apply(null, arguments);
  }
}

function fbq() {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq.apply(null, arguments);
  }
}

function pushDataLayer(event, payload) {
  if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event, ...payload });
  }
}

/**
 * Build a canonical booking URL with required UTM tags.
 *
 * @param {string} [location] — CTA placement label, appended as utm_content
 *   (e.g. "header", "hero", "mid_page", "bottom_cta", "popup").
 * @param {string} [category] — service category for a dedicated landing page.
 *   When provided, the URL becomes /book/<slug>?utm_... — distinct page-view
 *   for GA / Ads campaign tracking. Underscores in config category values
 *   ("jump_start") are normalized to hyphens in the URL ("jump-start").
 */
export function getBookingUrl(location, category) {
  const path = category
    ? `${BOOKING_PATH}/${String(category).replace(/_/g, '-')}`
    : BOOKING_PATH;
  const utm = location
    ? `${BOOKING_UTM_BASE}&utm_content=${encodeURIComponent(location)}`
    : BOOKING_UTM_BASE;
  return `${path}?${utm}`;
}

/**
 * Track a Call CTA click (tel: links).
 * Fires GA4 cta_call_click, Meta Pixel Contact, and GTM dataLayer push.
 */
export function trackCtaCallClick(location) {
  const ctaLocation = location || 'unknown';
  gtag('event', 'cta_call_click', {
    event_category: 'engagement',
    event_label: ctaLocation,
    send_to: GA_TRACKING_ID
  });
  fbq('track', 'Contact', {
    content_name: 'phone_call',
    cta_location: ctaLocation
  });
  pushDataLayer('cta_call_click', { cta_location: ctaLocation });
}

/**
 * Track an SMS CTA click (sms: links).
 * Fires GA4 cta_sms_click, Meta Pixel Contact, and GTM dataLayer push.
 */
export function trackCtaSmsClick(location) {
  const ctaLocation = location || 'unknown';
  gtag('event', 'cta_sms_click', {
    event_category: 'engagement',
    event_label: ctaLocation,
    send_to: GA_TRACKING_ID
  });
  fbq('track', 'Contact', {
    content_name: 'sms',
    cta_location: ctaLocation
  });
  pushDataLayer('cta_sms_click', { cta_location: ctaLocation });
}

/**
 * Track a Booking CTA click (anchors pointing at /book).
 * Fires GA4 cta_booking_click, Meta Pixel Lead, and GTM dataLayer push.
 */
export function trackCtaBookingClick(location) {
  const ctaLocation = location || 'unknown';
  gtag('event', 'cta_booking_click', {
    event_category: 'engagement',
    event_label: ctaLocation,
    send_to: GA_TRACKING_ID
  });
  fbq('track', 'Lead', {
    content_name: 'booking_cta',
    cta_location: ctaLocation
  });
  pushDataLayer('cta_booking_click', { cta_location: ctaLocation });
}

/**
 * Track CTA popup impressions, dismissals, and conversions (KAN: CTA popup).
 * `name` identifies the popup variant (e.g. "first_visit_offer").
 * `method` for dismissed: 'close_button' | 'outside_click' | 'escape'.
 */
export function trackPopupShown(name) {
  const popupName = name || 'cta_popup';
  gtag('event', 'popup_shown', {
    event_category: 'engagement',
    event_label: popupName,
    send_to: GA_TRACKING_ID
  });
  fbq('trackCustom', 'PopupShown', { popup_name: popupName });
  pushDataLayer('popup_shown', { popup_name: popupName });
}

export function trackPopupDismissed(name, method) {
  const popupName = name || 'cta_popup';
  const dismissMethod = method || 'unknown';
  gtag('event', 'popup_dismissed', {
    event_category: 'engagement',
    event_label: popupName,
    dismiss_method: dismissMethod,
    send_to: GA_TRACKING_ID
  });
  fbq('trackCustom', 'PopupDismissed', { popup_name: popupName, dismiss_method: dismissMethod });
  pushDataLayer('popup_dismissed', { popup_name: popupName, dismiss_method: dismissMethod });
}

export function trackPopupConverted(name) {
  const popupName = name || 'cta_popup';
  gtag('event', 'popup_converted', {
    event_category: 'engagement',
    event_label: popupName,
    send_to: GA_TRACKING_ID
  });
  fbq('track', 'Lead', { content_name: 'popup_cta', popup_name: popupName });
  pushDataLayer('popup_converted', { popup_name: popupName });
}

/**
 * Legacy custom GA event for "Book Now" CTAs. Kept so existing GA4 reports
 * built on `book_cta_click` keep working; also forwards to the new
 * `cta_booking_click` pipeline so KAN-28 dashboards see the same clicks.
 */
export function trackBookCtaClick(label) {
  gtag('event', 'book_cta_click', {
    event_category: 'engagement',
    event_label: label,
    send_to: GA_TRACKING_ID
  });
  fbq('trackCustom', 'BookCtaClick', {
    cta_location: label
  });
  trackCtaBookingClick(label);
}

/**
 * Track a /book page view as a conversion-eligible event
 */
export function trackBookPageView() {
  gtag('event', 'page_view', {
    page_path: '/book',
    page_title: 'Book — NorCal Mobile Oil Change',
    send_to: GA_TRACKING_ID
  });
  gtag('event', 'view_book_page', {
    event_category: 'engagement',
    event_label: 'Booking page viewed'
  });
  fbq('track', 'ViewContent', {
    content_name: 'Booking Page',
    content_category: 'booking'
  });
}

/**
 * Track a completed booking as a Google Ads conversion
 * @param {Object} booking - The booking object
 */
export function trackBookingConversion(booking) {
  gtag('event', 'conversion', {
    send_to: GA_TRACKING_ID,
    value: booking.totalPrice || 0,
    currency: 'USD',
    transaction_id: booking.confirmationNumber
  });

  // Also fire a custom event for Google Ads conversion actions
  gtag('event', 'purchase', {
    send_to: GA_TRACKING_ID,
    value: booking.totalPrice || 0,
    currency: 'USD',
    transaction_id: booking.confirmationNumber,
    items: [{
      item_name: booking.service || 'Oil Change',
      price: booking.totalPrice || 0,
      quantity: 1
    }]
  });

  // Meta Pixel: standard Lead + Purchase events for booking submission
  fbq('track', 'Lead', {
    content_name: booking.service || 'Oil Change',
    value: booking.totalPrice || 0,
    currency: 'USD'
  });
  fbq('track', 'Purchase', {
    content_name: booking.service || 'Oil Change',
    value: booking.totalPrice || 0,
    currency: 'USD'
  });
}

/**
 * Track phone call clicks as a Google Ads conversion (legacy hook).
 */
export function trackPhoneCallClick() {
  gtag('event', 'conversion', {
    send_to: GA_TRACKING_ID,
    value: 0,
    currency: 'USD'
  });
}

/**
 * Track SMS/text clicks as a Google Ads conversion (legacy hook).
 */
export function trackSmsClick() {
  gtag('event', 'conversion', {
    send_to: GA_TRACKING_ID,
    value: 0,
    currency: 'USD'
  });
  gtag('event', 'sms_click', {
    event_category: 'engagement',
    event_label: 'Text Us button'
  });
}

/**
 * Resolve a CTA location label from a clicked link. Reads `data-cta-location`
 * from the link itself or the nearest ancestor that exposes one.
 */
function resolveCtaLocation(link) {
  if (!link) return 'unknown';
  if (link.dataset && link.dataset.ctaLocation) return link.dataset.ctaLocation;
  const ancestor = link.closest('[data-cta-location]');
  if (ancestor && ancestor.dataset && ancestor.dataset.ctaLocation) {
    return ancestor.dataset.ctaLocation;
  }
  return 'unknown';
}

/**
 * Install a global click listener for tel: and sms: links.
 * Fires both the legacy Google Ads conversion event and the new
 * GA4 / Meta / dataLayer events used by KAN-28 dashboards.
 * Call once on app mount.
 */
export function initPhoneClickTracking() {
  document.addEventListener('click', (e) => {
    const telLink = e.target.closest('a[href^="tel:"]');
    if (telLink) {
      const loc = resolveCtaLocation(telLink);
      trackPhoneCallClick();
      trackCtaCallClick(loc);
      return;
    }
    const smsLink = e.target.closest('a[href^="sms:"]');
    if (smsLink) {
      const loc = resolveCtaLocation(smsLink);
      trackSmsClick();
      trackCtaSmsClick(loc);
    }
  });
}
